import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../components/Header";
import api, { errMsg } from "../lib/api";
import { toast, NoticeRegion } from "../lib/notices";
import { Loader2, Copy, Download, Sparkles, Send } from "lucide-react";
import { track } from "../lib/analytics";
import { useConfig } from "../lib/config";

const SECTIONS = [
  { key: "tagline", label: "Tagline" },
  { key: "landing_hero", label: "Landing Hero" },
  { key: "product_hunt_tagline", label: "Product Hunt Tagline" },
  { key: "product_hunt_first_comment", label: "PH First Comment" },
  { key: "linkedin_post", label: "LinkedIn Post" },
  { key: "email_announcement", label: "Email Announcement" },
  { key: "cold_outreach_email", label: "Cold Outreach" },
  { key: "youtube_description", label: "YouTube Description" },
  { key: "seo_title", label: "SEO Title" },
  { key: "seo_meta_description", label: "SEO Meta" },
  { key: "founder_pitch_script", label: "Founder Pitch Script" },
];

export default function LaunchKit() {
  const { featureEnabled } = useConfig();
  const { id } = useParams();
  const [kit, setKit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/projects/${id}/launch-kit`);
      setKit(r.data);
    } catch {
      setKit(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/projects/${id}/launch-kit`);
      setKit(r.data);
      track("launch_kit_generated", { project_id: id });
      toast.success("Launch kit generated");
    } catch (e) {
      toast.error(errMsg(e, "Launch kit requires Creator plan or higher"));
    } finally {
      setGenerating(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  const downloadMd = () => {
    const lines = ["# Launch Kit\n"];
    SECTIONS.forEach(({ key, label }) => {
      if (kit[key]) lines.push(`## ${label}\n\n${kit[key]}\n`);
    });
    if (kit.twitter_thread?.length) {
      lines.push("## Twitter Thread\n\n" + kit.twitter_thread.map((t, i) => `${i + 1}. ${t}`).join("\n\n"));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/markdown" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "launch-kit.md";
    a.click();
    track("export_clicked", { project_id: id, format: "launch-kit-md" });
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main id="main" tabIndex={-1} className="max-w-4xl mx-auto px-6 md:px-10 py-10">
        <NoticeRegion />
        <Link to={`/project/${id}`} className="text-xs uppercase tracking-widest text-ink-muted hover:text-ink-accent">← Back to project</Link>
        <div className="flex flex-wrap items-center justify-between gap-4 mt-4 mb-8">
          <div>
            <div className="marquee-tag mb-2">// LAUNCH KIT</div>
            <h1 className="font-display font-black type-title tracking-tighter">LAUNCH ASSETS</h1>
          </div>
          <div className="flex gap-2">
            {featureEnabled("social_publish") && (
              <Link to={`/project/${id}?publish=1`} className="brutal-btn brutal-btn-secondary" data-testid="btn-publish-from-kit">
                <Send size={16} /> Publish video
              </Link>
            )}
            {kit && (
              <button type="button" onClick={downloadMd} className="brutal-btn brutal-btn-secondary">
                <Download size={16} /> Download MD
              </button>
            )}
            <button type="button" onClick={generate} disabled={generating} className="brutal-btn">
              {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              {kit ? "Regenerate" : "Generate"}
            </button>
          </div>
        </div>

        {loading && <div className="text-center py-20 text-ink-muted">Loading…</div>}
        {!loading && !kit && (
          <div className="brutal-card p-12 text-center">
            <p className="text-ink-muted mb-6">Generate Product Hunt copy, LinkedIn posts, emails, ad scripts, and SEO meta from your demo project.</p>
            <button type="button" onClick={generate} disabled={generating} className="brutal-btn">
              {generating ? "Generating…" : "Generate Launch Kit"}
            </button>
            <p className="text-xs text-ink-subtle mt-4">Requires Creator plan or higher. Mock mode works without API keys.</p>
          </div>
        )}
        {kit && (
          <div className="space-y-6">
            {kit._mode && (
              <div className="text-xs uppercase tracking-widest text-ink-muted">
                Mode: {kit._mode === "mock" ? "Demo (no API key)" : kit._mode}
              </div>
            )}
            {SECTIONS.map(({ key, label }) => kit[key] && (
              <div key={key} className="brutal-card p-6">
                <div className="flex justify-between items-center mb-3">
                  <h2 className="text-xs uppercase tracking-widest text-ink-muted">{label}</h2>
                  <button type="button" onClick={() => copy(kit[key])} className="brutal-btn brutal-btn-ghost text-xs py-1 px-2">
                    <Copy size={14} /> Copy
                  </button>
                </div>
                <pre className="text-sm whitespace-pre-wrap font-mono-ui leading-relaxed">{kit[key]}</pre>
              </div>
            ))}
            {kit.twitter_thread?.length > 0 && (
              <div className="brutal-card p-6">
                <div className="flex justify-between items-center mb-3">
                  <h2 className="text-xs uppercase tracking-widest text-ink-muted">Twitter / X Thread</h2>
                  <button type="button" onClick={() => copy(kit.twitter_thread.join("\n\n"))} className="brutal-btn brutal-btn-ghost text-xs py-1 px-2">
                    <Copy size={14} /> Copy all
                  </button>
                </div>
                {kit.twitter_thread.map((t, i) => (
                  <p key={i} className="text-sm mb-3 pb-3 border-b brutal-border border-l-0 border-r-0 border-t-0 last:border-0">{t}</p>
                ))}
              </div>
            )}
            {kit.ad_copy?.length > 0 && (
              <div className="brutal-card p-6">
                <h2 className="text-xs uppercase tracking-widest text-ink-muted">Ad Copy</h2>
                {kit.ad_copy.map((a, i) => (
                  <p key={i} className="text-sm mt-3">{a}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
